import React, { useRef } from "react";
import {
  Box,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { getUserDateFormat } from "../../utils/formatDate";
const dateFormat = getUserDateFormat() || "MM/DD/YYYY";
// console.log("dateFormat", dateFormat);
const DateRangePickerField = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  errorMessage,
}) => {
  const startInputRef = useRef();
  const endInputRef = useRef();

  const handleOpenStartPicker = (e) => {
    e.stopPropagation();
    startInputRef.current?.showPicker?.();
  };

  const handleOpenEndPicker = (e) => {
    e.stopPropagation();
    endInputRef.current?.showPicker?.();
  };

  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* START DATE */}
      <FormControl fullWidth>
        <Typography sx={{ mb: 1 }}>Start Date</Typography>
        <TextField
          // label="Start Date"
          type="text"
          value={startDate ? dayjs(startDate).format(dateFormat) : ""}
          onClick={handleOpenStartPicker}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleOpenStartPicker}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 13.333V3.99967C14 3.26434 13.402 2.66634 12.6667 2.66634H11.3333V1.33301H10V2.66634H6V1.33301H4.66667V2.66634H3.33333C2.598 2.66634 2 3.26434 2 3.99967V13.333C2 14.0683 2.598 14.6663 3.33333 14.6663H12.6667C13.402 14.6663 14 14.0683 14 13.333ZM6 11.9997H4.66667V10.6663H6V11.9997ZM6 9.33301H4.66667V7.99967H6V9.33301ZM8.66667 11.9997H7.33333V10.6663H8.66667V11.9997ZM8.66667 9.33301H7.33333V7.99967H8.66667V9.33301ZM11.3333 11.9997H10V10.6663H11.3333V11.9997ZM11.3333 9.33301H10V7.99967H11.3333V9.33301ZM12.6667 5.99967H3.33333V4.66634H12.6667V5.99967Z"
                      fill="#183059"
                    />
                  </svg>
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              border: "2px solid #95A0B3",
              borderRadius: "16px",
              height: "43px",
              paddingLeft: "24px",
              paddingRight: "24px",
            },
            "& .MuiOutlinedInput-input": {
              padding: "12px 0", // Controls the vertical padding of the input text
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          fullWidth
          placeholder="Select date"
          readOnly
        />

        {/* Hidden native date input */}
        <input
          ref={startInputRef}
          type="date"
          value={startDate || ""}
          onChange={onStartDateChange}
          max={dayjs().format("YYYY-MM-DD")}
          style={{ display: "none" }}
        />

        <Box sx={{ minHeight: "20px" }}>
          {errorMessage?.startDate && (
            <Typography variant="body2" color="error">
              {errorMessage.startDate}
            </Typography>
          )}
        </Box>
      </FormControl>

      {/* END DATE */}
      <FormControl fullWidth>
        <Typography sx={{ mb: 1 }}>End Date</Typography>
        <TextField
          // label="End Date"
          type="text"
          value={endDate ? dayjs(endDate).format(dateFormat) : ""}
          onClick={handleOpenEndPicker}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleOpenEndPicker}>
                  {/* <CalendarMonthIcon /> */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 13.333V3.99967C14 3.26434 13.402 2.66634 12.6667 2.66634H11.3333V1.33301H10V2.66634H6V1.33301H4.66667V2.66634H3.33333C2.598 2.66634 2 3.26434 2 3.99967V13.333C2 14.0683 2.598 14.6663 3.33333 14.6663H12.6667C13.402 14.6663 14 14.0683 14 13.333ZM6 11.9997H4.66667V10.6663H6V11.9997ZM6 9.33301H4.66667V7.99967H6V9.33301ZM8.66667 11.9997H7.33333V10.6663H8.66667V11.9997ZM8.66667 9.33301H7.33333V7.99967H8.66667V9.33301ZM11.3333 11.9997H10V10.6663H11.3333V11.9997ZM11.3333 9.33301H10V7.99967H11.3333V9.33301ZM12.6667 5.99967H3.33333V4.66634H12.6667V5.99967Z"
                      fill="#183059"
                    />
                  </svg>
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              border: "2px solid #95A0B3",
              borderRadius: "16px",
              height: "43px",
              paddingLeft: "24px",
              paddingRight: "24px",
            },
            "& .MuiOutlinedInput-input": {
              padding: "12px 0", // Controls the vertical padding of the input text
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          fullWidth
          placeholder="Select date"
          readOnly
        />

        {/* Hidden native date input */}
        <input
          ref={endInputRef}
          type="date"
          value={endDate || ""}
          onChange={onEndDateChange}
          max={dayjs().format("YYYY-MM-DD")}
          min={startDate || ""} // 👈 prevents selecting earlier than start date
          style={{ display: "none" }}
        />

        <Box sx={{ minHeight: "20px" }}>
          {errorMessage?.endDate && (
            <Typography variant="body2" color="error">
              {errorMessage.endDate}
            </Typography>
          )}
        </Box>
      </FormControl>
    </Box>
  );
};

export default DateRangePickerField;